import { ReactNode } from 'react';
import { Menu, MenuItem } from '@szhsin/react-menu';
import { FaSearchMinus } from 'react-icons/fa';

import SvgCountryItalyIcon from '../Icons/CountryItalyIcon';
import SvgCountryJapanIcon from '../Icons/CountryJapanIcon';
import SvgCountryPortugalIcon from '../Icons/CountryPortugalIcon';
import SvgCountrySpainIcon from '../Icons/CountrySpainIcon';
import SvgCountryUsaIcon from '../Icons/CountryUsaIcon';
import TranslateIcon from '../Icons/TranslateIcon';
import { useAccessibilityContext } from '../Providers/AccessibilityProvider/AccessibilityContext';

export interface TranslateOptionProps {
  lang: string;
  icon: ReactNode;
  label: string;
  value: string;
}
const TranslateOptions: TranslateOptionProps[] = [
  {
    label: 'Español',
    icon: <SvgCountrySpainIcon className="h-[15px] w-[15px]" />,
    value: 'es',
    lang: 'es-MX',
  },
  {
    label: 'Inglés',
    icon: <SvgCountryUsaIcon className="h-[15px] w-[15px]" />,
    value: 'en',
    lang: 'en-US',
  },
  {
    label: 'Portugués',
    icon: <SvgCountryPortugalIcon className="h-[15px] w-[15px]" />,
    value: 'pt',
    lang: 'pt-PT',
  },
  {
    label: 'Italiano',
    icon: <SvgCountryItalyIcon className="h-[15px] w-[15px]" />,
    value: 'it',
    lang: 'it-IT',
  },
  {
    label: 'Japonés',
    icon: <SvgCountryJapanIcon className="h-[15px] w-[15px]" />,
    value: 'ja',
    lang: 'ja-JA',
  },
];

const TranslateOption = () => {
  const { setLanguaje, setLangTeller } = useAccessibilityContext();

  const handleTranslate = (value: string, lang: string) => {
    translate(value, lang, setLanguaje, setLangTeller);
  };

  return (
    <>
      <Menu
        menuButton={
          <div className="flex w-[200px] cursor-pointer items-center gap-3">
            <div className="flex h-[25px] w-[25px] items-center justify-center rounded-full bg-primary">
              <TranslateIcon className="h-3 w-3 fill-white" />
            </div>
            <label className="body-text1-regular cursor-pointer select-none text-[14px] !leading-[14px]">
              {'Traducir'}
            </label>
          </div>
        }
        onMenuChange={(e) => e.open}
      >
        <div className="notranslate flex gap-2 bg-white p-5 shadow-md">
          {TranslateOptions.map((item, index) => (
            <MenuItem
              key={index}
              className={'min-w-[50px] cursor-pointer hover:bg-gray-100'}
              onClick={() => handleTranslate(item.value, item.lang)}
            >
              <div className="flex flex-col items-center justify-center">
                {item.icon}
                <p className="text-[10px]">{item.label}</p>
              </div>
            </MenuItem>
          ))}
        </div>
      </Menu>
    </>
  );
};

export default TranslateOption;

/**
 * Función para cambiar el idioma usando el hack del widget de Google Translate.
 * Valida internamente que las opciones "es" y "en" existan en el select y, en caso de faltar, las agrega.
 */
export function translate(
  lang: string,
  langTeller: string,
  setLanguaje?: (lang: string) => void,
  setLangTeller?: (langTeller: string) => void
): boolean {
  // Actualiza el atributo lang del elemento <html>
  document.documentElement.setAttribute('lang', lang);

  // Validación interna: se verifica que existan las opciones en el select
  const select: HTMLSelectElement | null = document.querySelector('.goog-te-combo');
  if (!select) {
    console.error('Elemento select no encontrado');
    return false;
  }

  // Procede con el cambio de idioma
  try {
    const opcionEncontrada: HTMLOptionElement | null = select.querySelector(
      `option[value="${lang}"]`
    );
    if (!opcionEncontrada) {
      throw new Error(`Opción no encontrada para el idioma: ${lang}`);
    }

    // Verificar si el select actualmente está en 'es' y queremos cambiar a otro idioma
    const isFromSpanishToOther = select.value === 'es' && lang !== 'es';

    // Forzar siempre el cambio
    select.value = lang;
    select.dispatchEvent(new Event('change', { bubbles: true }));

    // Si venimos de español a otro idioma, forzar un segundo dispatch después de un delay
    // para asegurar que Google Translate procese la traducción correctamente
    if (isFromSpanishToOther) {
      setTimeout(() => {
        if (select.value !== lang) {
          select.value = lang;
          select.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }, 100);
    }

    if (setLanguaje) setLanguaje(lang);
    if (setLangTeller) setLangTeller(langTeller);
  } catch (error) {
    console.error('Error al intentar traducir:', error);
  }

  return false;
}
